import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
  EntityState,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import {
  buildIncludedUsers,
  mapV3BundleResourceToTaskBundle,
} from 'state/TasksBundle/helpers';
import { Status, TaskBundle } from 'utils/customTypes';
import {
  BulkCreateOrUpdateResult,
  BulkCreateTaskTemplateDependenciesParams,
  TaskTemplate,
  TaskTemplateForCreateOrUpdate,
  TaskTemplateDependencyEdge,
  BulkCreateTaskTemplateDependenciesResponse,
  FetchTaskTemplateDependenciesParams,
  BulkDeleteTaskTemplateDependenciesParams,
  TaskTemplateDependencyLink,
  TaskTemplateDependencyFailure,
} from 'utils/types/taskTemplate';
import { SLICE_STATUS } from 'utils/constants';
import {
  mapV3TemplatesToTaskTemplates,
  generateTaskTemplateDependencyEdges,
  mergeTemplateDependencies,
  extractDependencyEdgesFromV3Response,
} from './helpers';
import ActiveTaskBundleAPI from './activeTaskBundleApi';

const bundleTemplatesAdapter = createEntityAdapter<TaskTemplate>();
const dependenciesAdapter = createEntityAdapter<TaskTemplateDependencyEdge>();

interface ActiveTaskBundleState {
  status: Status;
  bundle: {
    status: Status;
    data: TaskBundle;
  };
  activeBundleTemplates: EntityState<TaskTemplate>;
  dependencyEdges: EntityState<TaskTemplateDependencyEdge>;
  dependencyManagement: {
    fetch: {
      status: Status;
      error?: string;
    };
    create: {
      status: Status;
      error?: string;
    };
    delete: {
      status: Status;
      error?: string;
      failures: TaskTemplateDependencyFailure[];
    };
  };
  upsertTemplatesStatus?: Status;
  deleteStatus?: Status;
}

/* ============================= INITIAL STATE ============================== */
const initialState: ActiveTaskBundleState = {
  status: SLICE_STATUS.IDLE,
  bundle: {
    status: SLICE_STATUS.IDLE,
    data: {} as TaskBundle,
  },
  activeBundleTemplates: bundleTemplatesAdapter.getInitialState(),
  dependencyEdges: dependenciesAdapter.getInitialState(),
  dependencyManagement: {
    fetch: {
      status: SLICE_STATUS.IDLE,
    },
    create: {
      status: SLICE_STATUS.IDLE,
    },
    delete: {
      status: SLICE_STATUS.IDLE,
      failures: [],
    },
  },
};

const activeBundleAPI = ActiveTaskBundleAPI;

/* ============================== REDUX THUNK =============================== */
export const fetchCurrentBundle = createAsyncThunk(
  'activeBundle/FETCH_CURRENT_BUNDLE',
  async (params: { bundleId: string; useNewApi?: boolean }) => {
    if (params.useNewApi) {
      const response = await activeBundleAPI.fetchTaskBundleV3(params.bundleId);
      const includedUsers = buildIncludedUsers(response.included ?? []);
      const taskBundle = mapV3BundleResourceToTaskBundle(
        response.data,
        includedUsers
      );
      return { taskBundle };
    }
    const response = await activeBundleAPI.fetchTaskBundle(params.bundleId);
    return response.data;
  }
);

export const updateBundle = createAsyncThunk(
  'activeBundle/UPDATE_BUNDLE',
  async (params: { bundle: TaskBundle; useNewApi?: boolean }) => {
    if (params.useNewApi) {
      await activeBundleAPI.updateTaskBundleV3(params.bundle);
      return { taskBundle: params.bundle };
    }
    const response = await activeBundleAPI.updateTaskBundle(params.bundle);
    return response.data;
  }
);

export const bulkCreateOrUpdateTemplates = createAsyncThunk(
  'activeBundle/BULK_CREATE_TEMPLATES',
  async (params: {
    templates: TaskTemplateForCreateOrUpdate[];
    bundleId: string;
    creatorIsNotified?: boolean;
    useNewApi?: boolean;
  }): Promise<BulkCreateOrUpdateResult[] | undefined> => {
    if (params.useNewApi) {
      return activeBundleAPI.bulkCreateOrUpdateTemplatesForBundleV3(
        params.templates,
        params.bundleId
      );
    }
    await activeBundleAPI.bulkCreateOrUpdateTemplatesForBundle(
      params.templates,
      params.bundleId,
      params.creatorIsNotified
    );
    return undefined;
  }
);

export const bulkCreateTaskTemplateDependencies = createAsyncThunk<
  BulkCreateTaskTemplateDependenciesResponse,
  BulkCreateTaskTemplateDependenciesParams,
  ThunkApi
>(
  'activeBundle/BULK_CREATE_TASK_TEMPLATE_DEPENDENCIES',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await activeBundleAPI.bulkCreateTaskTemplateDependenciesV3(params);
      return response;
    } catch (error) {
      return rejectWithValue(error as Error);
    }
  }
);

export const fetchTaskTemplateDependencies = createAsyncThunk<
  { templateId: string; edges: TaskTemplateDependencyEdge[] },
  Omit<FetchTaskTemplateDependenciesParams, 'direction'>,
  ThunkApi
>(
  'activeBundle/FETCH_TASK_TEMPLATE_DEPENDENCIES',
  async (params, { rejectWithValue }) => {
    try {
      const successorsResponse =
        await activeBundleAPI.fetchTaskTemplateDependencies({
          ...params,
          direction: 'successors',
        });
      const predecessorsResponse =
        await activeBundleAPI.fetchTaskTemplateDependencies({
          ...params,
          direction: 'predecessors',
        });
      const successorsDependencies = generateTaskTemplateDependencyEdges(
        params.bundleId,
        params.templateId,
        'successors',
        successorsResponse
      );
      const predecessorsDependencies = generateTaskTemplateDependencyEdges(
        params.bundleId,
        params.templateId,
        'predecessors',
        predecessorsResponse
      );
      return {
        templateId: params.templateId,
        edges: [...successorsDependencies, ...predecessorsDependencies],
      };
    } catch (error) {
      return rejectWithValue(error as Error);
    }
  }
);

export const fetchTaskTemplatesForBundle = createAsyncThunk(
  'activeBundle/FETCH_TASK_TEMPLATES_FOR_BUNDLE',
  async (params: {
    bundleId: string;
    useNewApi?: boolean;
    includeDependencies?: boolean;
  }) => {
    if (params.useNewApi) {
      const response = await activeBundleAPI.fetchTaskTemplatesForBundleV3(
        params.bundleId,
        params.includeDependencies
      );
      const taskTemplates = mapV3TemplatesToTaskTemplates(
        response.data ?? [],
        params.bundleId
      );
      const dependencyEdges = params.includeDependencies
        ? extractDependencyEdgesFromV3Response(
            response.data ?? [],
            params.bundleId
          )
        : [];
      return { taskTemplates, dependencyEdges };
    }
    const response = await activeBundleAPI.fetchTaskTemplatesForBundle(
      params.bundleId
    );
    return response.data;
  }
);

export const bulkDeleteTaskTemplatesForBundle = createAsyncThunk(
  'activeBundle/BULK_DELETE_TASK_TEMPLATES_FOR_BUNDLE',
  async (params: {
    templatesToDelete: string[];
    bundleId: string;
    useNewApi?: boolean;
  }) => {
    if (params.useNewApi) {
      await activeBundleAPI.bulkDeleteTaskTemplatesForBundleV3(
        params.templatesToDelete,
        params.bundleId
      );
      return params.templatesToDelete;
    }
    await activeBundleAPI.bulkDeleteTaskTemplatesForBundle(
      params.templatesToDelete,
      params.bundleId
    );
    return params.templatesToDelete;
  }
);

export const bulkDeleteTaskTemplateDependencies = createAsyncThunk<
  { removedLinks: TaskTemplateDependencyLink[]; failedDeps: string[] },
  BulkDeleteTaskTemplateDependenciesParams,
  ThunkApi
>(
  'activeBundle/BULK_DELETE_TASK_TEMPLATE_DEPENDENCIES',
  async (
    params: BulkDeleteTaskTemplateDependenciesParams,
    { rejectWithValue }
  ) => {
    try {
      const response = await activeBundleAPI.bulkDeleteTaskTemplateDependencies(
        params
      );
      return response;
    } catch (error) {
      return rejectWithValue(error as Error);
    }
  }
);

/* ================================= REDUCER ================================ */
const activeBundleSlice = createSlice({
  name: 'activeBundle',
  initialState,
  reducers: {
    RESET_CURRENT_BUNDLE: (state) => {
      state.bundle = {
        status: SLICE_STATUS.IDLE,
        data: {} as TaskBundle,
      };
      state.activeBundleTemplates = bundleTemplatesAdapter.getInitialState();
      state.dependencyEdges = dependenciesAdapter.getInitialState();
    },
    SET_TASK_TEMPLATES: (state, action: PayloadAction<TaskTemplate[]>) => {
      bundleTemplatesAdapter.setAll(
        state.activeBundleTemplates,
        action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bulkCreateOrUpdateTemplates.fulfilled, (state, action) => {
        state.upsertTemplatesStatus = SLICE_STATUS.IDLE;
      })
      .addCase(bulkCreateOrUpdateTemplates.pending, (state) => {
        state.upsertTemplatesStatus = SLICE_STATUS.LOADING;
      })
      .addCase(bulkCreateOrUpdateTemplates.rejected, (state) => {
        state.upsertTemplatesStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchCurrentBundle.fulfilled, (state, action) => {
        state.bundle.status = SLICE_STATUS.IDLE;
        state.bundle.data = action.payload.taskBundle;
      })
      .addCase(fetchCurrentBundle.pending, (state) => {
        state.bundle.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchCurrentBundle.rejected, (state) => {
        state.bundle.status = SLICE_STATUS.FAILED;
      })
      .addCase(updateBundle.fulfilled, (state, action) => {
        state.bundle.data = {
          ...state.bundle.data,
          ...action.payload.taskBundle,
        };
        state.bundle.status = SLICE_STATUS.IDLE;
      })
      .addCase(fetchTaskTemplatesForBundle.fulfilled, (state, action) => {
        state.status = SLICE_STATUS.IDLE;
        const payload = action.payload;
        if ('taskTemplates' in payload) {
          bundleTemplatesAdapter.setAll(
            state.activeBundleTemplates,
            payload.taskTemplates
          );
          dependenciesAdapter.setAll(
            state.dependencyEdges,
            payload.dependencyEdges ?? []
          );
        } else {
          bundleTemplatesAdapter.setAll(state.activeBundleTemplates, payload);
        }
      })
      .addCase(fetchTaskTemplatesForBundle.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchTaskTemplatesForBundle.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(bulkDeleteTaskTemplatesForBundle.fulfilled, (state, action) => {
        state.deleteStatus = SLICE_STATUS.IDLE;
        const deletedTemplates = action.payload;
        bundleTemplatesAdapter.removeMany(
          state.activeBundleTemplates,
          deletedTemplates
        );
        // Prune any dependency edges that reference deleted templates so that
        // local state stays consistent without waiting for a full refetch.
        const deletedSet = new Set(deletedTemplates);
        const staleEdgeIds = dependenciesAdapter
          .getSelectors()
          .selectAll(state.dependencyEdges)
          .filter(
            (edge) =>
              deletedSet.has(edge.dependencyId) ||
              deletedSet.has(edge.dependentId)
          )
          .map((edge) => edge.id);
        if (staleEdgeIds.length > 0) {
          dependenciesAdapter.removeMany(state.dependencyEdges, staleEdgeIds);
        }
      })
      .addCase(bulkDeleteTaskTemplatesForBundle.pending, (state) => {
        state.deleteStatus = SLICE_STATUS.LOADING;
      })
      .addCase(bulkDeleteTaskTemplatesForBundle.rejected, (state) => {
        state.deleteStatus = SLICE_STATUS.FAILED;
      })
      .addCase(fetchTaskTemplateDependencies.pending, (state) => {
        state.dependencyManagement.fetch.status = SLICE_STATUS.LOADING;
      })
      .addCase(fetchTaskTemplateDependencies.fulfilled, (state, action) => {
        state.dependencyManagement.fetch.status = SLICE_STATUS.IDLE;
        const currentEdges = dependenciesAdapter
          .getSelectors()
          .selectAll(state.dependencyEdges);
        const mergedEdges = mergeTemplateDependencies(
          action.payload.templateId,
          currentEdges,
          action.payload.edges
        );
        dependenciesAdapter.setAll(state.dependencyEdges, mergedEdges);
      })
      .addCase(fetchTaskTemplateDependencies.rejected, (state, action) => {
        state.dependencyManagement.fetch.status = SLICE_STATUS.FAILED;
        state.dependencyManagement.fetch.error = action.error.message;
      })
      .addCase(bulkCreateTaskTemplateDependencies.pending, (state) => {
        state.dependencyManagement.create.status = SLICE_STATUS.LOADING;
      })
      .addCase(bulkCreateTaskTemplateDependencies.rejected, (state, action) => {
        state.dependencyManagement.create.status = SLICE_STATUS.FAILED;
        state.dependencyManagement.create.error = action.error.message;
      })
      .addCase(
        bulkCreateTaskTemplateDependencies.fulfilled,
        (state, action) => {
          const { successes } = action.payload;
          dependenciesAdapter.upsertMany(state.dependencyEdges, successes);
          state.dependencyManagement.create.status = SLICE_STATUS.IDLE;
        }
      )
      .addCase(bulkDeleteTaskTemplateDependencies.pending, (state) => {
        state.dependencyManagement.delete.status = SLICE_STATUS.LOADING;
      })
      .addCase(bulkDeleteTaskTemplateDependencies.rejected, (state, action) => {
        state.dependencyManagement.delete.status = SLICE_STATUS.FAILED;
        state.dependencyManagement.delete.error = action.error.message;
      })
      .addCase(
        bulkDeleteTaskTemplateDependencies.fulfilled,
        (state, action) => {
          state.dependencyManagement.delete.status = SLICE_STATUS.IDLE;
          const { removedLinks } = action.payload;
          const edgeIdsToRemove: string[] = [];
          for (const link of removedLinks) {
            for (const dep of link.dependencies) {
              const edgeId =
                link.direction === 'predecessors'
                  ? `${dep}-${link.taskTemplateId}`
                  : `${link.taskTemplateId}-${dep}`;
              edgeIdsToRemove.push(edgeId);
            }
          }
          dependenciesAdapter.removeMany(
            state.dependencyEdges,
            edgeIdsToRemove
          );
        }
      );
  },
});

/* ============================= ACTIONS ============================== */
export const {
  RESET_CURRENT_BUNDLE: resetCurrentBundle,
  SET_TASK_TEMPLATES: setTaskTemplates,
} = activeBundleSlice.actions;

/* =============================== SELECTORS ================================ */

export const {
  selectAll: selectActiveBundleTemplates,
  selectById: selectActiveBundleTemplateById,
} = bundleTemplatesAdapter.getSelectors(
  (state: RootState) => state.activeTaskBundle.activeBundleTemplates
);

export const selectActiveBundle = (state: RootState) =>
  state.activeTaskBundle.bundle.data;

export const selectActiveBundleStatus = (state: RootState) =>
  state.activeTaskBundle.bundle.status;

export const selectActiveBundleTemplatesStatus = (state: RootState) =>
  state.activeTaskBundle.status;

export const selectDependencyManagementFetchStatus = (state: RootState) =>
  state.activeTaskBundle.dependencyManagement.fetch.status;

export const selectDependencyManagementCreateStatus = (state: RootState) =>
  state.activeTaskBundle.dependencyManagement.create.status;

export const { selectAll: selectDependencyEdges } =
  dependenciesAdapter.getSelectors(
    (state: RootState) => state.activeTaskBundle.dependencyEdges
  );

export const selectTemplateSuccessorsAndPredecessors = createSelector(
  [
    selectDependencyEdges,
    (state: RootState, templateId?: string) => templateId,
  ],
  (edges, templateId) => {
    if (!templateId) {
      return {
        successors: [],
        predecessors: [],
      };
    }
    const successors = edges
      .filter((edge) => edge.dependencyId === templateId)
      .map((edge) => edge.dependentId);
    const predecessors = edges
      .filter((edge) => edge.dependentId === templateId)
      .map((edge) => edge.dependencyId);
    return {
      successors,
      predecessors,
    };
  }
);

export default activeBundleSlice.reducer;
